import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControlLabel,
    Switch,
    Select,
    MenuItem,
    FormControl,
    FormHelperText,
    InputLabel,
    Grid,
    Alert,
    Skeleton,
    Tooltip,
    Tabs,
    Tab,
    Divider,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    ListItemSecondaryAction,
    Checkbox,
    ListItemIcon,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WebIcon from '@mui/icons-material/Web';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import MaterialSymbol from '../components/common/MaterialSymbol';
import { useNotify } from 'react-admin';
import { Link as RouterLink } from 'react-router-dom';
import { productService, ProductPrice } from '../services/productService';

// REACT_APP_API_URL ist auf Stage/Prod ohne /api gesetzt (z.B. https://oa-api.wsk.gmbh).
// Suffix normalisieren wie alle anderen Raw-fetch-Seiten, sonst 404 (#294).
const baseApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const API_URL = baseApiUrl.endsWith('/api') ? baseApiUrl : `${baseApiUrl}/api`;
const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL || window.location.origin;

interface LandingPageProduct {
    id: number;
    product: {
        id: number;
        title: string;
        type: string;
    };
    sortOrder: number;
    isFeatured: boolean;
    customTitle: string | null;
    badgeText: string | null;
}

interface LandingPage {
    id: number;
    uuid: string;
    slug: string;
    title: string;
    subtitle: string | null;
    description: string | null;
    layout: string;
    isActive: boolean;
    allowGuestCheckout: boolean;
    requireRegistration: boolean;
    heroImageUrl: string | null;
    primaryColor: string | null;
    secondaryColor: string | null;
    footerText: string | null;
    privacyPolicyUrl: string | null;
    termsUrl: string | null;
    features: LandingPageFeature[] | null;
    faq: LandingPageFaq[] | null;
    products: LandingPageProduct[];
    createdAt: string;
    updatedAt: string;
}

interface Product {
    id: number;
    title: string;
    type: string;
    isActive: boolean;
    prices?: ProductPrice[];
}

const renderProductPrice = (product: Product) => {
    const price = productService.getDefaultPrice(product);

    if (!price) {
        return <Chip size="small" color="warning" variant="outlined" label="Kein Preis" />;
    }

    return <Chip size="small" variant="outlined" label={productService.formatPrice(price)} />;
};

interface SelectedProduct {
    productId: number;
    sortOrder: number;
    isFeatured: boolean;
    associationId?: number;
}

interface LandingPageFeature {
    icon: string;
    title: string;
    description: string;
}

interface LandingPageFaq {
    question: string;
    answer: string;
}

const AVAILABLE_ICONS = [
    { value: 'school', label: 'Schule' },
    { value: 'code', label: 'Code' },
    { value: 'forum', label: 'Support' },
    { value: 'card_membership', label: 'Zertifikat' },
    { value: 'task_alt', label: 'Verifiziert' },
    { value: 'update', label: 'Aktuell' },
    { value: 'monitoring', label: 'Fortschritt' },
    { value: 'schedule', label: 'Flexibel' },
    { value: 'science', label: 'Praxis' },
    { value: 'settings', label: 'Technik' },
    { value: 'verified_user', label: 'Sicherheit' },
    { value: 'star', label: 'Stern' },
    { value: 'check_circle', label: 'Check' },
    { value: 'lightbulb', label: 'Idee' },
    { value: 'groups', label: 'Menschen' },
    { value: 'workspace_premium', label: 'Premium' },
];

const LandingPagesPage: React.FC = () => {
    const notify = useNotify();
    const [landingPages, setLandingPages] = useState<LandingPage[]>([]);
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingPage, setEditingPage] = useState<LandingPage | null>(null);
    const [formData, setFormData] = useState({
        slug: '',
        title: '',
        subtitle: '',
        description: '',
        layout: 'product_list',
        isActive: true,
        allowGuestCheckout: true,
        requireRegistration: true,
        heroImageUrl: '',
        primaryColor: '#1976d2',
        footerText: '',
        privacyPolicyUrl: '',
        termsUrl: '',
    });
    const [activeTab, setActiveTab] = useState(0);
    const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>([]);
    const [features, setFeatures] = useState<LandingPageFeature[]>([]);
    const [faqItems, setFaqItems] = useState<LandingPageFaq[]>([]);

    const getToken = () => localStorage.getItem('token');

    const fetchLandingPages = useCallback(async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_URL}/landing_pages?itemsPerPage=200`, {
                headers: {
                    'Authorization': `Bearer ${getToken()}`,
                    'Accept': 'application/ld+json',
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) throw new Error('Failed to fetch');

            const data = await response.json();
            // Handle JSON-LD formats: "hydra:member", "member", or plain array
            const pages = data['hydra:member'] || data['member'] || (Array.isArray(data) ? data : []);
            setLandingPages(pages);
        } catch (err) {
            setError('Fehler beim Laden der Landing Pages');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchProducts = useCallback(async () => {
        try {
            const response = await fetch(`${API_URL}/products?isActive=true&itemsPerPage=200`, {
                headers: {
                    'Authorization': `Bearer ${getToken()}`,
                    'Accept': 'application/ld+json',
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) throw new Error('Failed to fetch');

            const data = await response.json();
            setProducts(data['hydra:member'] || data['member'] || []);
        } catch (err) {
            console.error('Error fetching products:', err);
        }
    }, []);

    useEffect(() => {
        fetchLandingPages();
        fetchProducts();
    }, [fetchLandingPages, fetchProducts]);

    const handleOpenDialog = (page?: LandingPage) => {
        if (page) {
            setEditingPage(page);
            setFormData({
                slug: page.slug,
                title: page.title,
                subtitle: page.subtitle || '',
                description: page.description || '',
                layout: page.layout,
                isActive: page.isActive,
                allowGuestCheckout: page.allowGuestCheckout,
                requireRegistration: page.requireRegistration,
                heroImageUrl: page.heroImageUrl || '',
                primaryColor: page.primaryColor || '#1976d2',
                footerText: page.footerText || '',
                privacyPolicyUrl: page.privacyPolicyUrl || '',
                termsUrl: page.termsUrl || '',
            });
            // Load existing products
            const existingProducts = page.products?.map((lpp, index) => ({
                productId: typeof lpp.product === 'object' ? lpp.product.id : parseInt(String(lpp.product).split('/').pop() || '0'),
                sortOrder: lpp.sortOrder ?? index,
                isFeatured: lpp.isFeatured ?? false,
                associationId: lpp.id,
            })) || [];
            setSelectedProducts(existingProducts);
            // Load features and FAQ
            setFeatures(page.features || []);
            setFaqItems(page.faq || []);
        } else {
            setEditingPage(null);
            setFormData({
                slug: '',
                title: '',
                subtitle: '',
                description: '',
                layout: 'product_list',
                isActive: true,
                allowGuestCheckout: true,
                requireRegistration: true,
                heroImageUrl: '',
                primaryColor: '#1976d2',
                footerText: '',
                privacyPolicyUrl: '',
                termsUrl: '',
            });
            setSelectedProducts([]);
            setFeatures([]);
            setFaqItems([]);
        }
        setActiveTab(0);
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditingPage(null);
        setSelectedProducts([]);
        setFeatures([]);
        setFaqItems([]);
    };

    // Product selection handlers
    const isProductSelected = (productId: number) => {
        return selectedProducts.some(p => p.productId === productId);
    };

    const toggleProductSelection = (productId: number) => {
        if (isProductSelected(productId)) {
            setSelectedProducts(prev => prev.filter(p => p.productId !== productId));
        } else {
            setSelectedProducts(prev => [
                ...prev,
                { productId, sortOrder: prev.length, isFeatured: false }
            ]);
        }
    };

    const toggleFeatured = (productId: number) => {
        setSelectedProducts(prev => prev.map(p =>
            p.productId === productId ? { ...p, isFeatured: !p.isFeatured } : p
        ));
    };

    const moveProductUp = (productId: number) => {
        setSelectedProducts(prev => {
            const index = prev.findIndex(p => p.productId === productId);
            if (index <= 0) return prev;
            const newProducts = [...prev];
            [newProducts[index - 1], newProducts[index]] = [newProducts[index], newProducts[index - 1]];
            return newProducts.map((p, i) => ({ ...p, sortOrder: i }));
        });
    };

    const moveProductDown = (productId: number) => {
        setSelectedProducts(prev => {
            const index = prev.findIndex(p => p.productId === productId);
            if (index < 0 || index >= prev.length - 1) return prev;
            const newProducts = [...prev];
            [newProducts[index], newProducts[index + 1]] = [newProducts[index + 1], newProducts[index]];
            return newProducts.map((p, i) => ({ ...p, sortOrder: i }));
        });
    };

    const getProductById = (productId: number) => {
        return products.find(p => p.id === productId);
    };

    // Feature handlers
    const addFeature = () => {
        setFeatures(prev => [...prev, { icon: 'star', title: '', description: '' }]);
    };

    const updateFeature = (index: number, field: keyof LandingPageFeature, value: string) => {
        setFeatures(prev => prev.map((f, i) => i === index ? { ...f, [field]: value } : f));
    };

    const removeFeature = (index: number) => {
        setFeatures(prev => prev.filter((_, i) => i !== index));
    };

    // FAQ handlers
    const addFaqItem = () => {
        setFaqItems(prev => [...prev, { question: '', answer: '' }]);
    };

    const updateFaqItem = (index: number, field: keyof LandingPageFaq, value: string) => {
        setFaqItems(prev => prev.map((f, i) => i === index ? { ...f, [field]: value } : f));
    };

    const removeFaqItem = (index: number) => {
        setFaqItems(prev => prev.filter((_, i) => i !== index));
    };

    const generateSlug = (title: string) => {
        return title
            .toLowerCase()
            .replace(/[äöüß]/g, (char) => {
                const map: Record<string, string> = { ä: 'ae', ö: 'oe', ü: 'ue', ß: 'ss' };
                return map[char] || char;
            })
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-|-$/g, '');
    };

    const handleTitleChange = (title: string) => {
        setFormData(prev => ({
            ...prev,
            title,
            slug: !editingPage ? generateSlug(title) : prev.slug,
        }));
    };

    const saveProductAssociations = async (landingPageId: number) => {
        const keptAssociations = new Set(
            selectedProducts.map(sp => sp.associationId).filter((id): id is number => !!id)
        );

        for (const existing of editingPage?.products || []) {
            if (keptAssociations.has(existing.id)) {
                continue;
            }
            const response = await fetch(`${API_URL}/landing_page_products/${existing.id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${getToken()}` },
            });
            if (!response.ok && response.status !== 404) {
                throw new Error('Eine Produktzuordnung konnte nicht entfernt werden. Bitte erneut speichern.');
            }
        }

        for (const sp of selectedProducts) {
            const response = sp.associationId
                ? await fetch(`${API_URL}/landing_page_products/${sp.associationId}`, {
                    method: 'PATCH',
                    headers: {
                        'Authorization': `Bearer ${getToken()}`,
                        'Content-Type': 'application/merge-patch+json',
                        'Accept': 'application/ld+json',
                    },
                    body: JSON.stringify({
                        sortOrder: sp.sortOrder,
                        isFeatured: sp.isFeatured,
                    }),
                })
                : await fetch(`${API_URL}/landing_page_products`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${getToken()}`,
                        'Content-Type': 'application/ld+json',
                        'Accept': 'application/ld+json',
                    },
                    body: JSON.stringify({
                        landingPage: `/api/landing_pages/${landingPageId}`,
                        product: `/api/products/${sp.productId}`,
                        sortOrder: sp.sortOrder,
                        isFeatured: sp.isFeatured,
                    }),
                });

            if (!response.ok) {
                throw new Error('Eine Produktzuordnung konnte nicht gespeichert werden. Bitte erneut speichern.');
            }
        }
    };

    const handleSave = async () => {
        try {
            const url = editingPage
                ? `${API_URL}/landing_pages/${editingPage.id}`
                : `${API_URL}/landing_pages`;

            const method = editingPage ? 'PATCH' : 'POST';

            // Include features and FAQ in the request
            const dataToSend = {
                ...formData,
                features: features.length > 0 ? features.filter(f => f.title.trim()) : null,
                faq: faqItems.length > 0 ? faqItems.filter(f => f.question.trim()) : null,
            };

            const response = await fetch(url, {
                method,
                headers: {
                    'Authorization': `Bearer ${getToken()}`,
                    'Content-Type': method === 'PATCH'
                        ? 'application/merge-patch+json'
                        : 'application/ld+json',
                    'Accept': 'application/ld+json',
                },
                body: JSON.stringify(dataToSend),
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.detail || error['hydra:description'] || 'Fehler beim Speichern');
            }

            const savedPage = await response.json();
            const landingPageId = savedPage.id;

            if (landingPageId) {
                await saveProductAssociations(landingPageId);
            }

            notify(editingPage ? 'Landing Page aktualisiert' : 'Landing Page erstellt', { type: 'success' });
            handleCloseDialog();
            fetchLandingPages();
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Fehler beim Speichern';
            notify(message, { type: 'error' });
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('Sind Sie sicher, dass Sie diese Landing Page löschen möchten?')) {
            return;
        }

        try {
            const response = await fetch(`${API_URL}/landing_pages/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${getToken()}`,
                },
            });

            if (!response.ok) throw new Error('Failed to delete');

            notify('Landing Page geloescht', { type: 'success' });
            fetchLandingPages();
        } catch (err) {
            notify('Fehler beim Löschen', { type: 'error' });
        }
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        notify('Link kopiert', { type: 'info' });
    };

    const getPublicUrl = (slug: string) => `${FRONTEND_URL}/p/${slug}`;

    const openPreview = async (page: LandingPage) => {
        if (page.isActive) {
            window.open(getPublicUrl(page.slug), '_blank');
            return;
        }

        const tab = window.open('', '_blank');

        try {
            const response = await fetch(`${API_URL}/landing-pages/${page.id}/preview-token`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${getToken()}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ validForHours: 24 }),
            });

            if (!response.ok) throw new Error('Failed to create preview token');

            const data = await response.json();
            const url = `${getPublicUrl(page.slug)}?preview=${encodeURIComponent(data.previewToken)}`;

            if (tab) {
                tab.location.href = url;
            } else {
                copyToClipboard(url);
            }
        } catch (err) {
            console.error('Error creating preview token:', err);
            tab?.close();
            notify('Vorschau-Link konnte nicht erzeugt werden', { type: 'error' });
        }
    };

    if (loading) {
        return (
            <Box>
                <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WebIcon color="primary" />
                    Landing Pages
                </Typography>
                {[1, 2, 3].map((i) => (
                    <Skeleton key={i} variant="rectangular" height={60} sx={{ mb: 1 }} />
                ))}
            </Box>
        );
    }

    if (error) {
        return (
            <Box>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WebIcon color="primary" />
                    Landing Pages
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                >
                    Neue Landing Page
                </Button>
            </Box>

            {landingPages.length === 0 ? (
                <Card>
                    <CardContent sx={{ textAlign: 'center', py: 6 }}>
                        <WebIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" gutterBottom>
                            Keine Landing Pages vorhanden
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Erstellen Sie Landing Pages, um Kurse extern zu verkaufen.
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenDialog()}
                        >
                            Erste Landing Page erstellen
                        </Button>
                    </CardContent>
                </Card>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Titel</TableCell>
                                <TableCell>Slug / URL</TableCell>
                                <TableCell>Layout</TableCell>
                                <TableCell align="center">Produkte</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Aktionen</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {landingPages.map((page) => (
                                <TableRow key={page.id} hover>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="medium">
                                            {page.title}
                                        </Typography>
                                        {page.subtitle && (
                                            <Typography variant="caption" color="text.secondary">
                                                {page.subtitle}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                /p/{page.slug}
                                            </Typography>
                                            <Tooltip title="Link kopieren">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => copyToClipboard(getPublicUrl(page.slug))}
                                                >
                                                    <ContentCopyIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip
                                                title={
                                                    page.isActive
                                                        ? 'Vorschau öffnen'
                                                        : 'Vorschau öffnen (Test-Link, 24 Stunden gültig)'
                                                }
                                            >
                                                <IconButton
                                                    size="small"
                                                    aria-label="Vorschau öffnen"
                                                    onClick={() => openPreview(page)}
                                                >
                                                    <OpenInNewIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={page.layout === 'single_product' ? 'Einzelprodukt' : 'Produktliste'}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        {page.products?.length || 0}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={page.isActive ? 'Aktiv' : 'Inaktiv'}
                                            color={page.isActive ? 'success' : 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Bearbeiten">
                                            <IconButton aria-label="Bearbeiten" onClick={() => handleOpenDialog(page)}>
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Löschen">
                                            <IconButton
                                                aria-label="Löschen"
                                                color="error"
                                                onClick={() => handleDelete(page.id)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Edit Dialog */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    {editingPage ? 'Landing Page bearbeiten' : 'Neue Landing Page'}
                </DialogTitle>
                <DialogContent>
                    <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
                        <Tab label="Allgemein" />
                        <Tab label="Produkte" />
                        <Tab label="Inhalte" />
                        <Tab label="Design" />
                        <Tab label="Einstellungen" />
                    </Tabs>

                    {activeTab === 0 && (
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    label="Titel"
                                    value={formData.title}
                                    onChange={(e) => handleTitleChange(e.target.value)}
                                    required
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    label="Slug (URL)"
                                    value={formData.slug}
                                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                                    required
                                    helperText={`URL: ${getPublicUrl(formData.slug || 'slug')}`}
                                    InputProps={{
                                        sx: { fontFamily: 'monospace' }
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    label="Untertitel"
                                    value={formData.subtitle}
                                    onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    label="Beschreibung"
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    multiline
                                    rows={4}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <FormControl fullWidth>
                                    <InputLabel>Darstellung der Produkte</InputLabel>
                                    <Select
                                        value={formData.layout === 'single_product' ? 'single_product' : 'product_list'}
                                        label="Darstellung der Produkte"
                                        onChange={(e) => setFormData({ ...formData, layout: e.target.value })}
                                    >
                                        <MenuItem value="product_list">Produktliste — zweispaltig, Auswahl per Klick</MenuItem>
                                        <MenuItem value="single_product">Einzelprodukt — volle Breite, vorausgewählt</MenuItem>
                                    </Select>
                                    <FormHelperText>
                                        Steuert nur die Produktkarten. Hero-Bereich, Features und FAQ erscheinen
                                        immer, sobald sie unter &bdquo;Inhalte&ldquo; gefüllt sind.
                                    </FormHelperText>
                                </FormControl>
                            </Grid>
                        </Grid>
                    )}

                    {activeTab === 1 && (
                        <Box sx={{ mt: 1 }}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                                Wählen Sie die Produkte aus, die auf dieser Landing Page angeboten werden sollen.
                            </Typography>

                            {/* Selected Products */}
                            {selectedProducts.length > 0 && (
                                <Paper variant="outlined" sx={{ mb: 3, p: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Ausgewählte Produkte ({selectedProducts.length})
                                    </Typography>
                                    <List dense>
                                        {selectedProducts.map((sp, index) => {
                                            const product = getProductById(sp.productId);
                                            if (!product) return null;
                                            return (
                                                <ListItem key={sp.productId} sx={{ bgcolor: 'action.hover', mb: 0.5, borderRadius: 1 }}>
                                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                                        <DragIndicatorIcon color="disabled" />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={product.title}
                                                        secondary={
                                                            <Chip
                                                                size="small"
                                                                label={product.type === 'course' ? 'Kurs' : product.type === 'bundle' ? 'Bundle' : product.type}
                                                                sx={{ mt: 0.5 }}
                                                            />
                                                        }
                                                    />
                                                    <ListItemSecondaryAction>
                                                        <Tooltip title={sp.isFeatured ? 'Featured entfernen' : 'Als Featured markieren'}>
                                                            <IconButton size="small" onClick={() => toggleFeatured(sp.productId)}>
                                                                {sp.isFeatured ? <StarIcon color="warning" /> : <StarBorderIcon />}
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Nach oben">
                                                            <span>
                                                                <IconButton size="small" onClick={() => moveProductUp(sp.productId)} disabled={index === 0}>
                                                                    <span style={{ transform: 'rotate(-90deg)', display: 'inline-block' }}>›</span>
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                        <Tooltip title="Nach unten">
                                                            <span>
                                                                <IconButton size="small" onClick={() => moveProductDown(sp.productId)} disabled={index === selectedProducts.length - 1}>
                                                                    <span style={{ transform: 'rotate(90deg)', display: 'inline-block' }}>›</span>
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                        <Tooltip title="Entfernen">
                                                            <IconButton size="small" onClick={() => toggleProductSelection(sp.productId)} color="error">
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </ListItemSecondaryAction>
                                                </ListItem>
                                            );
                                        })}
                                    </List>
                                </Paper>
                            )}

                            {/* Available Products */}
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Verfügbare Produkte
                                </Typography>
                                <Button size="small" component={RouterLink} to="/products">
                                    Produktkatalog öffnen
                                </Button>
                            </Box>
                            <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
                                <List dense>
                                    {products.filter(p => p.isActive).map((product) => (
                                        <ListItemButton
                                            key={product.id}
                                            onClick={() => toggleProductSelection(product.id)}
                                            sx={{
                                                bgcolor: isProductSelected(product.id) ? 'action.selected' : 'transparent',
                                            }}
                                        >
                                            <ListItemIcon>
                                                <Checkbox
                                                    edge="start"
                                                    checked={isProductSelected(product.id)}
                                                    tabIndex={-1}
                                                    disableRipple
                                                />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={product.title}
                                                secondary={
                                                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                                        <Chip
                                                            size="small"
                                                            label={product.type === 'course' ? 'Kurs' : product.type === 'bundle' ? 'Bundle' : product.type}
                                                        />
                                                        {renderProductPrice(product)}
                                                    </Box>
                                                }
                                            />
                                        </ListItemButton>
                                    ))}
                                    {products.filter(p => p.isActive).length === 0 && (
                                        <ListItem>
                                            <ListItemText
                                                primary="Keine aktiven Produkte verfügbar"
                                                secondary={
                                                    <>
                                                        Legen Sie zuerst ein Produkt aus einem Kurs an —{' '}
                                                        <RouterLink to="/products">zum Produktkatalog</RouterLink>
                                                    </>
                                                }
                                            />
                                        </ListItem>
                                    )}
                                </List>
                            </Paper>
                        </Box>
                    )}

                    {activeTab === 2 && (
                        <Box sx={{ mt: 1 }}>
                            {/* Features Section */}
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                Features / "Was Sie erwartet"
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Diese Features werden auf der Landing Page unter "Was Sie erwartet" angezeigt.
                            </Typography>

                            {features.map((feature, index) => (
                                <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid size={{ xs: 12, sm: 3 }}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Icon</InputLabel>
                                                <Select
                                                    value={feature.icon}
                                                    label="Icon"
                                                    onChange={(e) => updateFeature(index, 'icon', e.target.value)}
                                                >
                                                    {AVAILABLE_ICONS.map(icon => (
                                                        <MenuItem key={icon.value} value={icon.value}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <MaterialSymbol name={icon.value} size={20} />
                                                                {icon.label}
                                                            </Box>
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 4 }}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Titel"
                                                value={feature.title}
                                                onChange={(e) => updateFeature(index, 'title', e.target.value)}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 4 }}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Beschreibung"
                                                value={feature.description}
                                                onChange={(e) => updateFeature(index, 'description', e.target.value)}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 1 }}>
                                            <IconButton color="error" onClick={() => removeFeature(index)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            ))}

                            <Button
                                startIcon={<AddIcon />}
                                onClick={addFeature}
                                sx={{ mb: 4 }}
                            >
                                Feature hinzufügen
                            </Button>

                            <Divider sx={{ my: 3 }} />

                            {/* FAQ Section */}
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                Häufige Fragen (FAQ)
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Diese Fragen werden auf der Landing Page als FAQ-Akkordeon angezeigt.
                            </Typography>

                            {faqItems.map((faq, index) => (
                                <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
                                    <Grid container spacing={2}>
                                        <Grid size={{ xs: 11 }}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Frage"
                                                value={faq.question}
                                                onChange={(e) => updateFaqItem(index, 'question', e.target.value)}
                                                sx={{ mb: 1 }}
                                            />
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Antwort"
                                                value={faq.answer}
                                                onChange={(e) => updateFaqItem(index, 'answer', e.target.value)}
                                                multiline
                                                rows={2}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 1 }}>
                                            <IconButton color="error" onClick={() => removeFaqItem(index)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            ))}

                            <Button
                                startIcon={<AddIcon />}
                                onClick={addFaqItem}
                            >
                                FAQ hinzufügen
                            </Button>
                        </Box>
                    )}

                    {activeTab === 3 && (
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    label="Hero-Bild URL"
                                    value={formData.heroImageUrl}
                                    onChange={(e) => setFormData({ ...formData, heroImageUrl: e.target.value })}
                                    placeholder="https://..."
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Primärfarbe"
                                    type="color"
                                    value={formData.primaryColor}
                                    onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    label="Footer Text"
                                    value={formData.footerText}
                                    onChange={(e) => setFormData({ ...formData, footerText: e.target.value })}
                                    multiline
                                    rows={2}
                                />
                            </Grid>
                        </Grid>
                    )}

                    {activeTab === 4 && (
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid size={{ xs: 12 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.isActive}
                                            onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                        />
                                    }
                                    label="Landing Page aktiv"
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.allowGuestCheckout}
                                            onChange={(e) => setFormData({ ...formData, allowGuestCheckout: e.target.checked })}
                                        />
                                    }
                                    label="Gast-Checkout erlauben"
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <FormControlLabel
                                    control={<Switch checked disabled />}
                                    label="Registrierung erforderlich"
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    Der Zugang zum Kurs hängt am Konto. Ein Kauf ohne Konto könnte nichts freischalten, deshalb wird immer eines angelegt.
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Divider sx={{ my: 2 }} />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    label="Datenschutzerklärung URL"
                                    value={formData.privacyPolicyUrl}
                                    onChange={(e) => setFormData({ ...formData, privacyPolicyUrl: e.target.value })}
                                    placeholder="https://..."
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    label="AGB URL"
                                    value={formData.termsUrl}
                                    onChange={(e) => setFormData({ ...formData, termsUrl: e.target.value })}
                                    placeholder="https://..."
                                />
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Abbrechen</Button>
                    <Button variant="contained" onClick={handleSave}>
                        {editingPage ? 'Speichern' : 'Erstellen'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default LandingPagesPage;
